import { useCallback } from 'react';

import { ICellRendererParams } from 'ag-grid-community';
import { Checkbox } from 'primereact/checkbox';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { useGetFreshStrawFertilizationsQuery } from '@/app/services/freshStrawRest';
import { useVerifyFreshShipmentMutation } from '@/services/fresh-shipment';

export const FreshShipmentVerifiedRenderer = (params: ICellRendererParams) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const date = searchParams.get('date') ?? '';
  const siteId = searchParams.get('sId') ?? '';
  const freshShipmentId = params.data?.freshShipmentId;
  const isVerified = params.data?.verified ?? false;

  const { data: fertilizations = [] } = useGetFreshStrawFertilizationsQuery(
    {
      fertilizationDate: date,
      fertilizationSite: siteId,
      freshShipmentId,
    },
    { skip: !date || !freshShipmentId },
  );

  const allFertsVerified =
    fertilizations.length > 0 &&
    fertilizations.every((f) => f.billableVerified === true);

  const { mutateAsync: verifyFreshShipment } = useVerifyFreshShipmentMutation();

  const handleChange = useCallback(() => {
    if (!freshShipmentId) return;
    const newVerified = !isVerified;
    verifyFreshShipment({
      freshShipmentId,
      verified: newVerified,
    }).then(() => {
      params.node.data.verified = newVerified;
      params.api.refreshCells({
        rowNodes: [params.node],
        columns: ['verified'],
        force: true,
      });
    });
  }, [
    freshShipmentId,
    isVerified,
    verifyFreshShipment,
    params.node,
    params.api,
  ]);

  return (
    <span
      className="flex w-full justify-center"
      title={
        !isVerified && !allFertsVerified
          ? t('billableShipped.allLinesMustBeVerified')
          : undefined
      }
    >
      <Checkbox
        checked={isVerified}
        disabled={!isVerified && !allFertsVerified}
        onChange={handleChange}
      />
    </span>
  );
};
